// caption.ts
// The caption an elevation renders under its image.

import type { Catalog, CatalogElevation } from '../../../../shared/utils/elevation-catalog';
import { ElevationView, extract, styleAliases } from '../helpers/extract';

const STYLE_DESCRIPTIONS: Record<string, string> = {
  aero: 'Clean lines and a single sloping roof give the Aero its minimalist calm.',
  scout: 'A classic gable and a welcoming porch give the Scout its farmhouse ease.',
  fjord: 'A dark shell wraps the roof and sides, and a warm wood face greets the street.'
};

const ROOF_DESCRIPTIONS: Record<string, string> = {
  slope: 'The slope roof pitches in one direction for a modern profile.',
  wedge: 'The wedge roof lifts the street-facing wall for a bold, angular line.',
  gable: 'The gable roof keeps the traditional peak that Detroit streets know.'
};

const SIDING_DESCRIPTIONS: Record<string, string> = {
  split: 'Two tones split the exterior, a light face over a darker base.',
  classic: 'One color carries across the whole exterior for a quiet, classic look.',
  wrap: 'The siding and the roof read as one continuous surface.'
};

export interface Caption {
  title: string;
  /** "Fjord (Scandinavian) · Gable roof · Wrap siding" */
  subtitle: string;
  /** "2 bed / 2.5 bath / 1,644 sq ft" */
  specs: string;
  description: string;
}

function subtitleFor(elevation: CatalogElevation, alias?: string): string {
  const parts: string[] = [];

  if (elevation.style) parts.push(alias ? `${elevation.style.name} (${alias})` : elevation.style.name);
  if (elevation.roof) parts.push(`${elevation.roof.name} roof`);
  if (elevation.siding) parts.push(`${elevation.siding.name} siding`);

  return parts.join(' · ');
}

function specsFor(view: ElevationView): string {
  const parts: string[] = [];

  if (view.beds) parts.push(`${view.beds} bed`);
  if (view.baths) parts.push(`${view.baths} bath`);
  if (view.sqFeet) parts.push(`${view.sqFeet} sq ft`);

  return parts.join(' / ');
}

function descriptionFor(elevation: CatalogElevation): string {
  const lines: string[] = [];
  const { style, roof, siding } = elevation;

  if (style && STYLE_DESCRIPTIONS[style.slug]) lines.push(STYLE_DESCRIPTIONS[style.slug]);
  else if (roof && ROOF_DESCRIPTIONS[roof.slug]) lines.push(ROOF_DESCRIPTIONS[roof.slug]);
  if (siding && SIDING_DESCRIPTIONS[siding.slug]) lines.push(SIDING_DESCRIPTIONS[siding.slug]);

  return lines.join(' ');
}

export function captionFor(elevation: CatalogElevation, view: ElevationView, alias?: string): Caption {
  return {
    title: view.displayName,
    subtitle: subtitleFor(elevation, alias),
    specs: specsFor(view),
    description: descriptionFor(elevation)
  };
}

export interface OutputsOptions {
  /** The catalog whose homePlans and styles tables the outputs read. */
  catalog: Catalog;
}

export const withOutputs = (options: OutputsOptions) => {
  const aliases = styleAliases(options.catalog);

  return (match: CatalogElevation[]): Record<string, unknown> => {
    const [first, ...others] = match;
    const view = extract(options.catalog, first);
    const alias = first.style ? aliases[first.style.slug] : undefined;

    return {
      elevation: first,
      others,
      ...captionFor(first, view, alias),
      beds: view.beds,
      baths: view.baths,
      'sq-feet': view.sqFeet,
      'hero-image': view.heroImage,
      'hero-alt': `${first.name}, exterior rendering`,
      'plan-name': first.plan ? first.plan.name : '',
      alias: alias || ''
    };
  };
};
